import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { routes, ROUTES } from '../config/routes'
import { RETURN_URL_STORAGE_KEY } from '../config/authConfig'
import { useAuth } from '../hooks/useAuth'

/**
 * Protected Route wrapper - redirects to home if not authenticated.
 * Waits for MSAL to finish initializing before deciding.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { inProgress } = useMsal();
  const location = useLocation();

  // MSAL is still loading accounts from the cache — show nothing yet
  if (inProgress !== InteractionStatus.None) {
    return null;
  }
  
  if (!isAuthenticated) {
    const returnUrl = location.pathname + location.search;
    // Also persist outside router state — it is lost across the Azure AD redirect.
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, returnUrl);
    return <Navigate to={ROUTES.Home} replace state={{ returnUrl }} />;
  }
  
  return <>{children}</>;
}

/**
 * Main application router
 * Dynamically generates routes from the single routes.tsx configuration
 */
export function AppRouter() {
  return (
    <Routes>
      {routes.map((route) => {
        const Component = route.element
        
        // Wrap protected routes with ProtectedRoute component
        if (route.protected) {
          return (
            <Route 
              key={route.path} 
              path={route.path} 
              element={
                <ProtectedRoute>
                  <Component />
                </ProtectedRoute>
              } 
            />
          )
        }
        
        return (
          <Route 
            key={route.path} 
            path={route.path} 
            element={<Component />} 
          />
        )
      })}
    </Routes>
  )
}
